import React, { useMemo, useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  TeamOutlined,
  SendOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  ReloadOutlined,
  FileTextOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Checkbox,
  Modal,
  Table,
  Tag,
  Form,
  Select,
  message,
  Tooltip,
  Empty,
  Pagination,
  Spin,
  Tabs,
} from "antd";
import CampaignRow from "./campaigns/CampaignRow";
import { useNavigate } from "react-router-dom";
import WhatsAppTemplateComposer from "./template-composer/WhatsAppTemplateComposer";
import TemplatePreview from "./template-composer/TemplatePreview";
import WhatsAppAccountHeaderPill from "./WhatsAppAccountHeaderPill";
import NoTableData from "../NoTableData";
import { getAuthMethod, postAuthMethod, patchAuthMethod, deleteAuthMethod, putAuthMethod } from "../../services/HttpServices";
import { ArrowLeft, Plus, Search, RefreshCw, FileText, Megaphone, Clock, Users } from "lucide-react";





const EmptyStateIllustration = ({ type = "campaigns" }) => (
  <div className="tw-relative tw-w-24 tw-h-24 tw-flex tw-items-center tw-justify-center tw-mb-4">
    <div className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-gradient-to-tr tw-from-[#5D44FF]/15 tw-via-[#5D44FF]/5 tw-to-transparent" />
    <div className="tw-absolute tw-inset-2.5 tw-rounded-full tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-100" />
    {type === "templates" ? (
      <svg className="tw-w-10 tw-h-10 tw-text-[#5D44FF] tw-relative tw-z-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ) : (
      <svg className="tw-w-10 tw-h-10 tw-text-[#5D44FF] tw-relative tw-z-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    )}
    <div className="tw-absolute -tw-bottom-0.5 -tw-right-0.5 tw-size-7 tw-rounded-full tw-bg-[#5D44FF] tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-md">
      <SearchOutlined className="tw-text-[11px]" />
    </div>
  </div>
);

const TemplateRow = ({ template, onArchive }) => {
  let statusBadge = (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-slate-100 tw-text-slate-600 tw-font-inter">
      <span className="tw-size-1.5 tw-rounded-full tw-bg-slate-400" />
      {template.status}
    </span>
  );

  if (template.status === "APPROVED") {
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-[#ECFDF5] tw-text-[#007A55] tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-[#12B76A]" />
        {template.status}
      </span>
    );
  } else if (template.status === "PENDING") {
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-amber-50 tw-text-amber-700 tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-amber-500" />
        {template.status}
      </span>
    );
  } else if (template.status === "REJECTED") {
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-red-50 tw-text-red-700 tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-red-500" />
        {template.status}
      </span>
    );
  }

  return (
    <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-p-5 tw-shadow-sm hover:tw-border-[#5D44FF]/40 tw-transition-all tw-font-inter">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-start tw-gap-3.5 tw-min-w-0">
          <div className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-xl tw-flex-shrink-0 tw-bg-[#EEF0FF] tw-text-[#5D44FF]">
            <FileText size={18} />
          </div>
          <div className="tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
              <h4 className="tw-text-sm md:tw-text-base tw-font-bold tw-text-slate-800 tw-m-0 tw-truncate tw-font-inter">
                {template.name}
              </h4>
              {statusBadge}
            </div>

            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2.5 tw-text-xs tw-font-medium tw-text-slate-500 tw-flex-wrap tw-font-inter">
              <span className="tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-slate-700 tw-font-semibold tw-text-[11px]">
                {template.category}
              </span>
              <span className="tw-text-slate-300">•</span>
              <span>
                Language: <strong className="tw-text-slate-700">{template.language}</strong>
              </span>
              {(() => {
                if (template.components) {
                  let comps = template.components;
                  if (typeof comps === "string") {
                    try {
                      comps = JSON.parse(comps);
                    } catch (e) {
                      comps = [];
                    }
                  }
                  if (Array.isArray(comps)) {
                    const headerComp = comps.find((c) => c.type === "HEADER");
                    if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                      return (
                        <>
                          <span className="tw-text-slate-300">•</span>
                          <span>
                            Media Type: <strong className="tw-text-slate-700">{headerComp.format}</strong>
                          </span>
                        </>
                      );
                    }
                  }
                }
                return null;
              })()}
              {(() => {
                const dateVal =
                  template.createdDate ||
                  template.createdAt ||
                  template.updatedAt ||
                  template.lastModifiedDate ||
                  template.lastUpdatedDate;

                if (!dateVal) return null;

                try {
                  const d = new Date(dateVal);
                  if (isNaN(d.getTime())) return null;

                  const now = new Date();
                  const diffMs = Math.max(0, now - d);
                  const diffMins = Math.floor(diffMs / (1000 * 60));
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                  let relativeText = "";
                  if (diffMins < 1) relativeText = "Just now";
                  else if (diffMins < 60) relativeText = `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
                  else if (diffHours < 24) relativeText = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
                  else if (diffDays < 7) relativeText = `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
                  else relativeText = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

                  const formattedTime = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                  const isUpdated = Boolean(
                    template.updatedAt || template.lastModifiedDate || template.lastUpdatedDate
                  );
                  const labelPrefix = isUpdated ? "Updated" : "Created";

                  return (
                    <>
                      <span className="tw-text-slate-300">•</span>
                      <span className="tw-text-slate-500 tw-inline-flex tw-items-center tw-gap-1">
                        <Clock size={12} className="tw-text-slate-400" />
                        <span>{labelPrefix}: <strong className="tw-text-slate-700">{relativeText}</strong> ({d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {formattedTime})</span>
                      </span>
                    </>
                  );
                } catch (e) {
                  return null;
                }
              })()}
            </div>

            <div className="tw-mt-3.5 tw-p-3.5 tw-bg-slate-50 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-text-xs tw-text-slate-700 tw-font-normal tw-leading-relaxed tw-whitespace-pre-wrap tw-font-inter">
              {(() => {
                if (template.bodyText) return template.bodyText;
                if (template.components) {
                  let comps = template.components;
                  if (typeof comps === "string") {
                    try {
                      comps = JSON.parse(comps);
                    } catch (e) {
                      comps = [];
                    }
                  }
                  if (Array.isArray(comps)) {
                    const bodyComp = comps.find((c) => c.type === "BODY");
                    if (bodyComp) return bodyComp.text;
                  }
                }
                return "No body text";
              })()}
            </div>
          </div>
        </div>
        
        {onArchive && (
          <div className="tw-flex-shrink-0 tw-ml-4">
            <Button 
              type="text" 
              danger
              onClick={() => onArchive(template)}
              className="tw-text-xs tw-font-semibold tw-font-inter hover:!tw-bg-red-50"
            >
              Archive
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const WhatsAppCampaigns = ({ onBack, onCreateCampaign, connectionData }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  const [mainView, setMainView] = useState("campaigns");
  const [templateStatusFilter, setTemplateStatusFilter] = useState("ALL");
  const [activeTab, setActiveTab] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    language: "en_US",
    category: "UTILITY",
    bodyText: "",
  });
  const [templateCounts, setTemplateCounts] = useState({});
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [modalTab, setModalTab] = useState("preview");

  const getTemplateComponents = (campaign) => {
    if (!campaign) return [];
    const t = campaign.templateDetails || campaign.whatsappMessageTemplate || campaign.templateObj;
    let comps = t?.components || campaign.components;
    if (typeof comps === "string") {
      try {
        comps = JSON.parse(comps);
      } catch (e) {
        comps = [];
      }
    }
    if (Array.isArray(comps) && comps.length > 0) return comps;

    const body = t?.bodyText || t?.body || campaign.bodyText || campaign.body;
    if (body) {
      return [{ type: "BODY", text: body }];
    }
    return [];
  };

  const getSampleValuesFromMapping = (mappingConfig) => {
    if (!mappingConfig) return {};
    let config = mappingConfig;
    if (typeof config === "string") {
      try {
        config = JSON.parse(config);
      } catch (e) {
        config = {};
      }
    }
    const sampleValues = { body: {}, header: null };
    if (config.body) {
      Object.keys(config.body).forEach((key) => {
        const item = config.body[key];
        if (item) {
          if (item.type === "constant" && item.value) {
            sampleValues.body[key] = item.value;
          } else if (item.type === "audience_field" && item.attribute) {
            sampleValues.body[key] = `[${item.attribute}]`;
          } else if (item.value) {
            sampleValues.body[key] = item.value;
          }
        }
      });
    }
    if (config.header) {
      if (typeof config.header === "string") {
        sampleValues.header = config.header;
      } else if (config.header.value) {
        sampleValues.header = config.header.value;
      }
    }
    return sampleValues;
  };
  
  const [executionLogs, setExecutionLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchExecutionLogs = (campaignId, page = 0, size = 10) => {
    setLogsLoading(true);
    getAuthMethod(`/api/whatsapp-campaigns/${campaignId}/execution-logs?page=${page}&size=${size}&sort=sentAt,desc`)
      .then((res) => {
        setExecutionLogs(res.data || []);
        setLogsPagination({
          current: page + 1,
          pageSize: size,
          total: parseInt(res.headers["x-total-count"] || 0, 10),
        });
        setLogsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch execution logs", err);
        setLogsLoading(false);
      });
  };

  const handleTableChange = (pagination) => {
    fetchExecutionLogs(selectedCampaign.id, pagination.current - 1, pagination.pageSize);
  };

  const templatesMapRef = useRef({});
  const [templatesMap, setTemplatesMap] = useState({});

  const fetchAllTemplatesMap = async () => {
    try {
      const res = await getAuthMethod("/api/whatsapp/templates?size=500");
      const list = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      const map = {};
      list.forEach((t) => {
        if (t && t.id) {
          map[t.id] = t;
        }
      });
      templatesMapRef.current = map;
      setTemplatesMap(map);
      return map;
    } catch (err) {
      console.error("Failed to load templates map", err);
      return templatesMapRef.current || {};
    }
  };

  const [templatePage, setTemplatePage] = useState(1);
  const [templatePageSize, setTemplatePageSize] = useState(5);
  const [templateTotal, setTemplateTotal] = useState(0);

  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignPageSize, setCampaignPageSize] = useState(5);
  const [campaignTotal, setCampaignTotal] = useState(0);

  const fetchCampaigns = async (page = campaignPage, size = campaignPageSize, tab = activeTab, search = searchValue) => {
    setLoadingCampaigns(true);
    let currentMap = templatesMapRef.current;
    if (!currentMap || Object.keys(currentMap).length === 0) {
      currentMap = await fetchAllTemplatesMap();
    }
    const apiPage = (page || 1) - 1;
    let url = `/api/whatsapp-campaigns?page=${apiPage}&size=${size}&sort=lastModifiedDate,desc`;

    getAuthMethod(url)
      .then((res) => {
        let rawData = [];
        let totalCount = 0;

        if (Array.isArray(res.data)) {
          rawData = res.data;
          totalCount = parseInt(
            res.headers["x-total-count"] || res.headers?.get?.("x-total-count") || rawData.length,
            10
          );
        } else if (res.data && Array.isArray(res.data.content)) {
          rawData = res.data.content;
          totalCount =
            res.data.totalElements ||
            parseInt(res.headers["x-total-count"] || 0, 10) ||
            rawData.length;
        }

        const currentMapLatest = templatesMapRef.current || currentMap || {};

        const mappedData = rawData.map((c) => {
          let statusTone = "draft";
          if (c.status === "COMPLETED") statusTone = "sent";
          else if (c.status === "SCHEDULED" || c.status === "RUNNING") statusTone = "scheduled";
          else if (c.status === "PAUSED") statusTone = "approval";

          const calcPercent = (count) => (!c.totalCount ? 0 : Math.round(((count || 0) / c.totalCount) * 100));

          const templateId =
            c.whatsappMessageTemplateId ||
            c.templateId ||
            c.whatsappTemplateId ||
            (typeof c.template === "number" ? c.template : null);
          const matchedTemplate = currentMapLatest && templateId ? currentMapLatest[templateId] : null;

          const templateDisplayName =
            c.whatsappMessageTemplateName ||
            c.templateName ||
            c.whatsappTemplateName ||
            c.whatsappMessageTemplate?.name ||
            (typeof c.template === "object" ? c.template?.name : null) ||
            matchedTemplate?.name ||
            (typeof c.template === "string" && c.template !== "Template Message" ? c.template : null) ||
            (templateId ? `Template #${templateId}` : "") ||
            "Template";

          return {
            ...c,
            statusTone,
            audience: c.totalCount || 0,
            template: templateDisplayName,
            templateDetails:
              matchedTemplate ||
              (typeof c.template === "object" ? c.template : null) ||
              c.whatsappMessageTemplate ||
              null,
            sentAt: c.scheduledDate
              ? dayjs(c.scheduledDate).format("MMM DD, YYYY hh:mm A")
              : c.createdDate
              ? dayjs(c.createdDate).format("MMM DD, YYYY hh:mm A")
              : "-",
            delivered: calcPercent(c.deliveredCount),
            read: calcPercent(c.readCount),
            clicked: 0,
          };
        });

        mappedData.sort((a, b) => {
          const dateA = new Date(
            a.lastModifiedDate || a.updatedDate || a.lastModifiedAt || a.createdDate || 0
          ).getTime();
          const dateB = new Date(
            b.lastModifiedDate || b.updatedDate || b.lastModifiedAt || b.createdDate || 0
          ).getTime();
          return dateB - dateA;
        });

        // Filter fallback if endpoint returns unpaginated full list
        let finalData = mappedData;
        if (totalCount > size && mappedData.length > size) {
          const filtered = mappedData.filter((campaign) => {
            if (tab === "archived") {
              if (!campaign.isArchived) return false;
            } else {
              if (campaign.isArchived) return false;
              if (tab === "sent" && campaign.statusTone !== "sent") return false;
              if (tab === "scheduled" && campaign.statusTone !== "scheduled") return false;
              if (tab === "draft" && campaign.statusTone !== "draft") return false;
              if (tab === "pending" && campaign.statusTone !== "approval" && campaign.status !== "Pending") return false;
            }
            if (search && search.trim()) {
              return campaign.name?.toLowerCase().includes(search.trim().toLowerCase());
            }
            return true;
          });
          setCampaignTotal(filtered.length);
          const start = (page - 1) * size;
          finalData = filtered.slice(start, start + size);
        } else {
          setCampaignTotal(totalCount || mappedData.length);
        }

        setCampaigns(finalData);
      })
      .catch((err) => console.error("Failed to fetch campaigns", err))
      .finally(() => setLoadingCampaigns(false));
  };

  const sortLatestTemplates = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const dateA = new Date(
        a.createdDate || a.createdAt || a.updatedAt || a.lastModifiedDate || 0
      ).getTime();
      const dateB = new Date(
        b.createdDate || b.createdAt || b.updatedAt || b.lastModifiedDate || 0
      ).getTime();
      if (dateA && dateB && dateA !== dateB) return dateB - dateA;
      const idA = typeof a.id === "number" ? a.id : parseInt(a.id, 10);
      const idB = typeof b.id === "number" ? b.id : parseInt(b.id, 10);
      if (!isNaN(idA) && !isNaN(idB)) return idB - idA;
      return 0;
    });
  };

  const fetchTemplates = (page = templatePage, size = templatePageSize, statusFilter = templateStatusFilter, search = searchValue) => {
    setLoadingTemplates(true);
    const apiPage = page - 1;
    let url = `/api/whatsapp/templates?page=${apiPage}&size=${size}&sort=createdDate,desc`;
    if (statusFilter === "ARCHIVED") {
      url += `&isArchived=true`;
    } else if (statusFilter && statusFilter !== "ALL") {
      url += `&status.equals=${statusFilter}`;
    }
    if (search && search.trim()) {
      url += `&name.contains=${encodeURIComponent(search.trim())}`;
    }

    getAuthMethod(url)
      .then((res) => {
        let rawData = [];
        let totalCount = 0;

        if (Array.isArray(res.data)) {
          rawData = res.data;
          totalCount = parseInt(
            res.headers["x-total-count"] || res.headers?.get?.("x-total-count") || rawData.length,
            10
          );
        } else if (res.data && Array.isArray(res.data.content)) {
          rawData = res.data.content;
          totalCount =
            res.data.totalElements ||
            parseInt(res.headers["x-total-count"] || 0, 10) ||
            rawData.length;
        }

        const sorted = sortLatestTemplates(rawData);

        // Filter fallback if backend endpoint returns unpaginated array
        let finalTemplates = sorted;
        if (totalCount > size && sorted.length > size) {
          const filtered = sorted.filter((t) => {
            if (statusFilter === "ARCHIVED") {
              if (!t.isArchived) return false;
            } else {
              if (t.isArchived) return false;
              if (statusFilter !== "ALL" && t.status !== statusFilter) return false;
            }
            if (search && search.trim()) {
              const q = search.trim().toLowerCase();
              return t.name?.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q);
            }
            return true;
          });
          setTemplateTotal(filtered.length);
          const start = (page - 1) * size;
          finalTemplates = filtered.slice(start, start + size);
        } else {
          setTemplateTotal(totalCount || sorted.length);
        }

        setTemplates(finalTemplates);
      })
      .catch((err) => console.error("Failed to fetch templates", err))
      .finally(() => setLoadingTemplates(false));
      
    // Fetch counts from backend
    getAuthMethod("/api/whatsapp/templates/counts")
      .then(res => {
        setTemplateCounts(res.data || {});
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchTemplates();
    fetchCampaigns();
  }, []);

  const handleArchiveTemplate = (template) => {
    Modal.confirm({
      title: "Archive Template",
      content: `Are you sure you want to archive "${template.name}"?`,
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: () => {
        putAuthMethod(`/api/whatsapp/templates`, template.id, { ...template, isArchived: true })
          .then(() => {
            message.success("Template archived successfully.");
            fetchTemplates();
          })
          .catch(err => {
            console.error("Failed to archive template", err);
            message.error("Failed to archive template.");
          });
      },
    });
  };

  const handleRowAction = (key, campaign) => {
    if (key === "pause") {
      postAuthMethod(`/api/whatsapp-campaigns/${campaign.id}/pause`, {})
        .then(() => {
          message.success("Campaign paused successfully.");
          fetchCampaigns();
        })
        .catch(err => message.error("Failed to pause campaign: " + (err.response?.data?.title || err.response?.data?.message || err.message)));
    } else if (key === "resume") {
      postAuthMethod(`/api/whatsapp-campaigns/${campaign.id}/resume`, {})
        .then(() => {
          message.success("Campaign resumed successfully.");
          fetchCampaigns();
        })
        .catch(err => message.error("Failed to resume campaign: " + (err.response?.data?.title || err.response?.data?.message || err.message)));
    } else if (key === "launch_now") {
      postAuthMethod(`/api/whatsapp-campaigns/${campaign.id}/launch`, {})
        .then(() => {
          message.success("Campaign launched successfully.");
          fetchCampaigns();
        })
        .catch(err => message.error("Failed to launch campaign: " + (err.response?.data?.title || err.response?.data?.message || err.message)));
    } else if (key === "edit") {
      navigate(`/whatsapp-campaigns/edit/${campaign.id}`, { state: { editCampaign: campaign } });
    } else if (key === "duplicate") {
      Modal.confirm({
        title: "Duplicate Campaign",
        content: `Are you sure you want to duplicate "${campaign.name}"?`,
        okText: "Yes, Duplicate",
        onOk: () => {
          navigate("/whatsapp-campaigns/create", { state: { duplicateCampaign: campaign } });
        }
      });
    } else if (key === "stats" || key === "preview") {
      let enrichedCampaign = { ...campaign };
      const templateId = campaign.whatsappMessageTemplateId || campaign.templateId || campaign.whatsappTemplateId;
      if (!enrichedCampaign.templateDetails && templateId && templatesMapRef.current[templateId]) {
        enrichedCampaign.templateDetails = templatesMapRef.current[templateId];
      }
      setSelectedCampaign(enrichedCampaign);
      setModalTab(key === "stats" ? "stats" : "preview");
      setStatsModalVisible(true);
      fetchExecutionLogs(campaign.id, 0, 10);

      if (!enrichedCampaign.templateDetails?.components && templateId) {
        getAuthMethod(`/api/whatsapp/templates?id.equals=${templateId}`)
          .then((res) => {
            const list = Array.isArray(res.data) ? res.data : (res.data?.content || []);
            if (list.length > 0) {
              setSelectedCampaign((prev) => ({
                ...prev,
                templateDetails: list[0],
              }));
            }
          })
          .catch(() => {});
      }
    } else if (key === "archive") {
      Modal.confirm({
        title: "Archive Campaign",
        content: "Are you sure you want to archive this campaign?",
        okText: "Yes, Archive",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          patchAuthMethod(`/api/whatsapp-campaigns/${campaign.id}/archive`, null, {})
            .then(() => {
              message.success("Campaign archived successfully.");
              fetchCampaigns();
            })
            .catch(err => message.error("Failed to archive campaign."));
        }
      });
    } else if (key === "delete") {
      Modal.confirm({
        title: "Delete Campaign",
        content: `Are you sure you want to delete "${campaign.name}"?`,
        okText: "Yes, Delete",
        okType: "danger",
        onOk: () => {
          postAuthMethod(`/api/whatsapp-campaigns/${campaign.id}/delete`, {})
            .then(() => {
              message.success("Campaign deleted successfully.");
              fetchCampaigns();
            })
            .catch(err => message.error("Failed to delete campaign: " + (err.response?.data?.title || err.message)));
        }
      });
    } else {
      message.info(`Action ${key} not implemented yet`);
    }
  };

  const handleSyncTemplates = () => {
    setSyncingTemplates(true);
    postAuthMethod("/api/whatsapp/templates/sync")
      .then((res) => {
        message.success("Templates synced successfully!");
        setTemplates(sortLatestTemplates(res.data || []));
      })
      .catch((err) => {
        console.error("Sync templates error", err);
        message.error("Failed to sync templates.");
      })
      .finally(() => setSyncingTemplates(false));
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.bodyText) {
      message.error("Name and Body are required.");
      return;
    }
    setCreatingTemplate(true);
    postAuthMethod("/api/whatsapp/templates", newTemplate)
      .then((res) => {
        message.success("Template created and synced successfully!");
        setTemplates(sortLatestTemplates(res.data || []));
        setIsCreateModalVisible(false);
        setNewTemplate({
          name: "",
          language: "en_US",
          category: "UTILITY",
          bodyText: "",
        });
      })
      .catch((err) => {
        console.error("Create template error", err);
        message.error(
          "Failed to create template: " +
            (err.response?.data?.title || err.response?.data?.message || err.message),
        );
      })
      .finally(() => setCreatingTemplate(false));
  };

  // Calculate dynamic counts based on loaded campaigns and templates
  const counts = useMemo(() => {
    const activeCampaigns = campaigns.filter(c => !c.isArchived);
    return {
      all: activeCampaigns.length,
      sent: activeCampaigns.filter((c) => c.statusTone === "sent").length,
      scheduled: activeCampaigns.filter((c) => c.statusTone === "scheduled").length,
      draft: activeCampaigns.filter((c) => c.statusTone === "draft").length,
      pending: activeCampaigns.filter((c) => c.statusTone === "approval" || c.status === "Pending").length,
      archived: campaigns.filter(c => c.isArchived).length,
      templates: templates.length,
      templatesApproved: templates.filter(t => t.status === "APPROVED").length,
      templatesPending: templates.filter(t => t.status === "PENDING").length,
      templatesRejected: templates.filter(t => t.status === "REJECTED").length,
    };
  }, [campaigns, templates]);

  const campaignTabs = [
    {
      key: "all",
      label: "All Campaigns",
      count: counts.all,
      tooltip: "View all WhatsApp campaigns",
    },
    {
      key: "sent",
      label: "Sent",
      count: counts.sent,
      tooltip: "View sent campaigns",
    },
    {
      key: "scheduled",
      label: "Scheduled",
      count: counts.scheduled,
      tooltip: "View scheduled campaigns",
    },
    {
      key: "draft",
      label: "Draft",
      count: counts.draft,
      tooltip: "View draft campaigns",
    },
    {
      key: "pending",
      label: "Pending Approval",
      count: counts.pending,
      tooltip: "View campaigns pending approval",
    },
    {
      key: "archived",
      label: "Archived",
      count: counts.archived,
      tooltip: "View archived campaigns",
    },
  ];

  const templateTabs = [
    {
      key: "ALL",
      label: "All Templates",
      count: templateCounts["ALL"] || 0,
      tooltip: "View all message templates",
    },
    {
      key: "APPROVED",
      label: "Approved",
      count: templateCounts["APPROVED"] || 0,
      tooltip: "View approved templates ready for campaigns",
    },
    {
      key: "PENDING",
      label: "Pending Review",
      count: templateCounts["PENDING"] || 0,
      tooltip: "View templates pending Meta review",
    },
    {
      key: "REJECTED",
      label: "Rejected",
      count: templateCounts["REJECTED"] || 0,
      tooltip: "View rejected templates",
    },
    {
      key: "ARCHIVED",
      label: "Archived",
      count: templateCounts["ARCHIVED"] || 0,
      tooltip: "View archived templates",
    },
  ];

  // Filter campaigns by tab & search query
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      // Archive filter
      if (activeTab === "archived") {
        if (!campaign.isArchived) return false;
      } else {
        if (campaign.isArchived) return false;
        
        // Tab filter
        if (activeTab === "sent" && campaign.statusTone !== "sent") return false;
        if (activeTab === "scheduled" && campaign.statusTone !== "scheduled")
          return false;
        if (activeTab === "draft" && campaign.statusTone !== "draft")
          return false;
        if (
          activeTab === "pending" &&
          campaign.statusTone !== "approval" &&
          campaign.status !== "Pending"
        )
          return false;
      }

      // Search query filter
      return campaign.name?.toLowerCase().includes(searchValue.trim().toLowerCase());
    });
  }, [searchValue, activeTab, campaigns]);

  // Filter templates by category/status & search query
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Status filter
      if (templateStatusFilter === "ARCHIVED") {
        if (!template.isArchived) return false;
      } else {
        if (template.isArchived) return false;
        if (templateStatusFilter !== "ALL" && template.status !== templateStatusFilter) {
          return false;
        }
      }
      // Search query filter
      return (
        template.name
          ?.toLowerCase()
          .includes(searchValue.trim().toLowerCase()) ||
        template.category
          ?.toLowerCase()
          .includes(searchValue.trim().toLowerCase())
      );
    });
  }, [searchValue, templates, templateStatusFilter]);

  const handleTemplatePageChange = (page, size) => {
    setTemplatePage(page);
    if (size) setTemplatePageSize(size);
    fetchTemplates(page, size || templatePageSize, templateStatusFilter, searchValue);
  };

  const fetchAccountStatusAndRefetchCampaigns = (page = campaignPage, size = campaignPageSize, tab = activeTab, search = searchValue) => {
    fetchCampaigns(page, size, tab, search);

    getAuthMethod("/api/whatsapp/accounts/status")
      .then((res) => {
        const statusData = res?.data;
        if (statusData && (statusData.onboardingState === "COMPLETED" || statusData.connected)) {
          setTimeout(() => {
            fetchCampaigns(page, size, tab, search);
          }, 30000);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch WhatsApp account status", err);
        setTimeout(() => {
          fetchCampaigns(page, size, tab, search);
        }, 30000);
      });
  };

  const handleCampaignPageChange = (page, size) => {
    setCampaignPage(page);
    if (size) setCampaignPageSize(size);
    fetchAccountStatusAndRefetchCampaigns(page, size || campaignPageSize, activeTab, searchValue);
  };

  useEffect(() => {
    setTemplatePage(1);
    fetchTemplates(1, templatePageSize, templateStatusFilter, searchValue);
  }, [templateStatusFilter, searchValue]);

  useEffect(() => {
    setCampaignPage(1);
    fetchAccountStatusAndRefetchCampaigns(1, campaignPageSize, activeTab, searchValue);
  }, [activeTab]);

  return (
    <div className="p-20 tw-font-inter tw-bg-white tw-min-h-full tw-overflow-y-auto">
      <div className="tw-max-w-[1400px] tw-mx-auto">
        {/* Header Block matching Search and Listing Pages */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-3.5">
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 hover:tw-bg-slate-100/60 tw-text-slate-600 tw-transition-colors tw-shadow-xs tw-flex-shrink-0"
              onClick={onBack || (() => navigate(-1))}
              role="button"
              aria-label="Back"
            >
              <ArrowLeft size={18} />
            </div>
            <div>
              <h2 className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-font-inter tw-tracking-tight tw-mb-0">
                {mainView === "templates" ? "Message Templates" : "Campaigns"}
              </h2>
              <p className="tw-text-xs tw-text-slate-500 tw-font-medium tw-font-inter tw-mt-1 tw-mb-0">
                {mainView === "templates" 
                  ? "Create, sync, and manage Meta WhatsApp message templates."
                  : "Broadcast WhatsApp messages and track performance."}
              </p>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
            <WhatsAppAccountHeaderPill phoneNumbers={connectionData?.phoneNumbers || []} />

            {mainView === "templates" ? (
              <>
                <Button
                  onClick={handleSyncTemplates}
                  loading={syncingTemplates}
                  className="!tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 tw-flex tw-items-center tw-gap-2 tw-text-xs md:tw-text-sm"
                >
                  <RefreshCw className={`tw-w-4 tw-h-4 ${syncingTemplates ? "tw-animate-spin" : ""}`} />
                  Sync Templates
                </Button>
                <Button
                  type="primary"
                  onClick={() => setIsCreateModalVisible(true)}
                  className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-text-white !tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-text-xs md:tw-text-sm tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
                >
                  <Plus size={16} />
                  Create Template
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/campaigns/audience")}
                  className="!tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 tw-flex tw-items-center tw-gap-2 tw-text-xs md:tw-text-sm"
                >
                  <Users size={16} className="tw-text-slate-500" />
                  Manage Audiences
                </Button>
                <Button
                  type="primary"
                  onClick={onCreateCampaign}
                  className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-text-white !tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-text-xs md:tw-text-sm tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
                >
                  <Plus size={16} />
                  Create Campaign
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Top-Level Tabs matching Search Page Tab Design */}
        <div id="tabscompany" className="tw-mb-5">
          <Tabs
            activeKey={mainView}
            onChange={(key) => {
              setMainView(key);
              if (key === "campaigns") {
                setActiveTab("all");
              } else {
                setTemplateStatusFilter("ALL");
              }
            }}
            className="tw-font-inter !tw-mb-0 [&_.ant-tabs-nav]:!tw-mb-0 [&_.ant-tabs-nav]:!tw-w-full"
            items={[
              {
                key: "campaigns",
                label: (
                  <span className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-semibold tw-py-1">
                    <Megaphone size={16} />
                    <span>Campaigns</span>
                    <span
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-bold tw-rounded-full tw-transition-colors ${
                        mainView === "campaigns"
                          ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                          : "tw-bg-slate-100 tw-text-slate-600"
                      }`}
                    >
                      {counts.all}
                    </span>
                  </span>
                ),
              },
              {
                key: "templates",
                label: (
                  <span className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-semibold tw-py-1">
                    <FileText size={16} />
                    <span>Message Templates</span>
                    <span
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-bold tw-rounded-full tw-transition-colors ${
                        mainView === "templates"
                          ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                          : "tw-bg-slate-100 tw-text-slate-600"
                      }`}
                    >
                      {counts.templates}
                    </span>
                  </span>
                ),
              },
            ]}
          />
        </div>

        {/* Status Filter Tabs with Search Input on Right */}
        <div id="tabscompany" className="tw-mb-6">
          <Tabs
            activeKey={mainView === "campaigns" ? activeTab : templateStatusFilter}
            onChange={(key) => {
              if (mainView === "campaigns") {
                setActiveTab(key);
              } else {
                setTemplateStatusFilter(key);
              }
            }}
            tabBarExtraContent={{
              right: (
                <div className="tw-flex tw-items-center tw-gap-3 tw-pb-1">
                  <Input
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    prefix={<Search className="tw-text-slate-400 tw-w-4 tw-h-4" />}
                    placeholder={mainView === "templates" ? "Search templates..." : "Search list..."}
                    className="!tw-h-[38px] !tw-rounded-xl tw-border-slate-200 hover:tw-border-slate-300 focus:tw-border-[#5D44FF] tw-w-[220px] sm:tw-w-[280px] tw-font-inter tw-text-xs"
                    allowClear
                  />
                </div>
              ),
            }}
            className="tw-font-inter !tw-mb-0 [&_.ant-tabs-nav]:!tw-mb-0 [&_.ant-tabs-nav]:!tw-w-full"
            items={(mainView === "campaigns" ? campaignTabs : templateTabs).map((tab) => {
              const isActive = (mainView === "campaigns" ? activeTab === tab.key : templateStatusFilter === tab.key);
              return {
                key: tab.key,
                label: (
                  <Tooltip title={tab.tooltip}>
                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-py-1">
                      <span>{tab.label}</span>
                      <span
                        className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-rounded-full tw-transition-colors ${
                          isActive
                            ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                            : "tw-bg-slate-100 tw-text-slate-500"
                        }`}
                      >
                        {tab.count}
                      </span>
                    </span>
                  </Tooltip>
                ),
              };
            })}
          />
        </div>

      {/* Content Area - Responsive Grid */}
      <div>
        {mainView === "templates" ? (
          loadingTemplates ? (
            <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-6 tw-shadow-sm">
              <Spin size="large" />
              <div className="tw-mt-4 tw-text-xs tw-font-medium tw-text-slate-500">
                Loading templates...
              </div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-text-center tw-shadow-sm">
              <EmptyStateIllustration type="templates" />
              <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0">
                No templates found
              </h3>
              <p className="tw-text-xs tw-text-slate-400 tw-mt-1.5 tw-max-w-xs tw-m-0">
                Try syncing templates from Meta or create a new message template.
              </p>
              <div className="tw-flex tw-gap-2.5 tw-mt-5">
                <Button
                  onClick={handleSyncTemplates}
                  loading={syncingTemplates}
                  className="!tw-h-[38px] !tw-rounded-xl !tw-px-4 tw-text-xs tw-font-semibold tw-border-slate-200 hover:tw-bg-slate-50"
                >
                  Sync Templates
                </Button>
                <Button
                  type="primary"
                  onClick={() => setIsCreateModalVisible(true)}
                  className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-h-[38px] !tw-rounded-xl !tw-px-4 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
                >
                  <Plus size={16} />
                  Create Template
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="tw-grid tw-grid-cols-1 tw-gap-5">
                {templates.map((template) => (
                  <TemplateRow
                    key={template.id || template.name}
                    template={template}
                    onArchive={handleArchiveTemplate}
                  />
                ))}
              </div>
              {templateTotal > templatePageSize && (
                <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-6 tw-py-3.5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-sm">
                  <span className="tw-text-xs tw-text-slate-500 tw-font-medium">
                    Showing {(templatePage - 1) * templatePageSize + 1}-
                    {Math.min(templatePage * templatePageSize, templateTotal)}{" "}
                    of {templateTotal} templates
                  </span>
                  <Pagination
                    current={templatePage}
                    pageSize={templatePageSize}
                    total={templateTotal}
                    onChange={handleTemplatePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )
        ) : loadingCampaigns ? (
          <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-6 tw-shadow-sm">
            <Spin size="large" />
            <div className="tw-mt-4 tw-text-xs tw-font-medium tw-text-slate-500">
              Loading campaigns...
            </div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-text-center tw-shadow-sm">
            <EmptyStateIllustration type="campaigns" />
            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0">
              {searchValue ? `No campaigns matching "${searchValue}"` : "No campaigns found"}
            </h3>
            <p className="tw-text-xs tw-text-slate-400 tw-mt-1.5 tw-max-w-xs tw-m-0">
              {searchValue 
                ? "We couldn't find any campaigns matching your search term." 
                : "Try adjusting your filters or create a new campaign to get started."}
            </p>
            <div className="tw-flex tw-items-center tw-gap-3 tw-mt-5">
              {searchValue && (
                <Button
                  onClick={() => setSearchValue("")}
                  className="!tw-h-[38px] !tw-rounded-xl !tw-border-slate-200 !tw-px-4 tw-text-xs tw-font-bold tw-text-slate-600 hover:!tw-bg-slate-50"
                >
                  Clear Search
                </Button>
              )}
              <Button
                type="primary"
                onClick={onCreateCampaign}
                className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-text-white !tw-h-[38px] !tw-rounded-xl !tw-px-4 !tw-text-xs md:!tw-text-sm !tw-font-bold tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
              >
                <Plus size={16} />
                Create Campaign
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-5">
              {campaigns.map((campaign) => (
                <CampaignRow key={campaign.id} campaign={campaign} onAction={handleRowAction} />
              ))}
            </div>
            {campaignTotal > campaignPageSize && (
              <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-6 tw-py-3.5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-sm">
                <span className="tw-text-xs tw-text-slate-500 tw-font-medium">
                  Showing {(campaignPage - 1) * campaignPageSize + 1}-
                  {Math.min(campaignPage * campaignPageSize, campaignTotal)}{" "}
                  of {campaignTotal} campaigns
                </span>
                <Pagination
                  current={campaignPage}
                  pageSize={campaignPageSize}
                  total={campaignTotal}
                  onChange={handleCampaignPageChange}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create New WhatsApp Template Modal (Composer) */}
      <WhatsAppTemplateComposer 
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={() => {
          setIsCreateModalVisible(false);
          fetchTemplates();
        }}
      />

      {/* Stats & Preview Modal */}
      <Modal
        title={
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-font-bold tw-text-base">Campaign Details:</span>
            <span className="tw-text-base tw-font-semibold tw-text-[#5D44FF]">{selectedCampaign?.name || ''}</span>
          </div>
        }
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={null}
        width={940}
        centered
      >
        {selectedCampaign && (
          <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-3 tw-pb-2">
            {/* Template & Campaign Info Banner */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-bg-[#F8FAFC] tw-px-4 tw-py-3 tw-rounded-xl tw-border tw-border-solid tw-border-[#E2E8F0]">
              <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <FileTextOutlined className="tw-text-[#5D44FF] tw-text-base" />
                <span className="tw-text-xs tw-text-gray-500 tw-font-medium">Template Used:</span>
                <span className="tw-text-sm tw-font-semibold tw-text-[#1E293B]">
                  {selectedCampaign.template || "—"}
                </span>
                {selectedCampaign.templateDetails?.category && (
                  <Tag className="!tw-ml-1 !tw-text-[10px] !tw-rounded-md !tw-border-0 !tw-bg-[#EEF2FF] !tw-text-[#4F46E5] !tw-font-semibold">
                    {selectedCampaign.templateDetails.category}
                  </Tag>
                )}
                {selectedCampaign.templateDetails?.language && (
                  <Tag className="!tw-text-[10px] !tw-rounded-md !tw-border-0 !tw-bg-gray-100 !tw-text-gray-600">
                    {selectedCampaign.templateDetails.language}
                  </Tag>
                )}
              </div>
              <div className="tw-flex tw-items-center tw-gap-3.5 tw-text-xs tw-text-gray-500 tw-flex-wrap">
                {selectedCampaign.status && (
                  <span>
                    Status: <strong className="tw-text-gray-800 tw-uppercase">{selectedCampaign.status}</strong>
                  </span>
                )}
                {selectedCampaign.sentAt && selectedCampaign.sentAt !== "-" && (
                  <span>
                    Date: <strong className="tw-text-gray-800">{selectedCampaign.sentAt}</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Tabs for Template Preview and Analytics & Logs */}
            <Tabs
              activeKey={modalTab}
              onChange={setModalTab}
              items={[
                {
                  key: "preview",
                  label: (
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-font-semibold">
                      <EyeOutlined /> Template Preview
                    </span>
                  ),
                  children: (
                    <div className="tw-py-4 tw-px-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#F8FAFC] tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200">
                      {(() => {
                        const comps = getTemplateComponents(selectedCampaign);
                        const sampleVals = getSampleValuesFromMapping(selectedCampaign.mappingConfig);
                        if (comps.length === 0) {
                          return (
                            <div className="tw-py-12 tw-text-center tw-text-gray-400 tw-text-sm">
                              No preview available for this template.
                            </div>
                          );
                        }
                        return (
                          <div className="tw-w-full tw-flex tw-justify-center">
                            <TemplatePreview
                              components={comps}
                              sampleValues={sampleVals}
                              compact={true}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  ),
                },
                {
                  key: "stats",
                  label: (
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-font-semibold">
                      <BarChartOutlined /> Analytics & Logs
                    </span>
                  ),
                  children: (
                    <div className="tw-flex tw-flex-col tw-gap-5 tw-pt-1">
                      {/* Stat Cards Row */}
                      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-5 tw-gap-3.5">
                        <div className="tw-bg-gray-50 tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100">
                          <div className="tw-text-gray-500 tw-text-xs tw-font-medium">Total Audience</div>
                          <div className="tw-text-2xl tw-font-bold tw-text-text-primary tw-mt-1.5">{selectedCampaign.totalCount || selectedCampaign.audience || 0}</div>
                        </div>
                        <div className="tw-bg-gray-50 tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100">
                          <div className="tw-text-gray-500 tw-text-xs tw-font-medium">Sent Count</div>
                          <div className="tw-text-2xl tw-font-bold tw-text-blue-600 tw-mt-1.5">{selectedCampaign.sentCount || 0}</div>
                        </div>
                        <div className="tw-bg-green-50/70 tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-green-100">
                          <div className="tw-text-green-600 tw-text-xs tw-font-medium">Delivered Count</div>
                          <div className="tw-text-2xl tw-font-bold tw-text-green-700 tw-mt-1.5">{selectedCampaign.deliveredCount || 0}</div>
                        </div>
                        <div className="tw-bg-purple-50/70 tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-purple-100">
                          <div className="tw-text-purple-600 tw-text-xs tw-font-medium">Read Count</div>
                          <div className="tw-text-2xl tw-font-bold tw-text-purple-700 tw-mt-1.5">{selectedCampaign.readCount || 0}</div>
                        </div>
                        <div className="tw-bg-red-50/70 tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-red-100">
                          <div className="tw-text-red-600 tw-text-xs tw-font-medium">Failed Count</div>
                          <div className="tw-text-2xl tw-font-bold tw-text-red-700 tw-mt-1.5">{selectedCampaign.failedCount || 0}</div>
                        </div>
                      </div>

                      {/* Execution Logs Table Section */}
                      <div className="tw-mt-2">
                        <h3 className="tw-text-base tw-font-bold tw-mb-4 tw-font-inter tw-text-text-primary">Execution Logs</h3>
                        <Table 
                          dataSource={executionLogs}
                          loading={logsLoading}
                          pagination={logsPagination}
                          onChange={handleTableChange}
                          rowKey="id"
                          size="small"
                          className="tw-custom-table tw-custom-table-no-border"
                          locale={{
                            emptyText: <NoTableData text="No Data Available" />
                          }}
                          columns={[
                            {
                              title: 'NAME',
                              dataIndex: 'memberName',
                              key: 'memberName',
                              width: '22%',
                              render: (name) => <span className="tw-font-medium tw-text-text-primary">{name || "Recipient"}</span>
                            },
                            {
                              title: 'PHONE NUMBER',
                              dataIndex: 'memberPhoneNumber',
                              key: 'memberPhoneNumber',
                              width: '20%',
                              render: (phone) => <span className="tw-text-sub-text">{phone || "-"}</span>
                            },
                            {
                              title: 'STATUS',
                              dataIndex: 'status',
                              key: 'status',
                              width: '15%',
                              render: (status) => {
                                let color = 'default';
                                if (status === 'SENT') color = 'blue';
                                else if (status === 'DELIVERED') color = 'green';
                                else if (status === 'READ') color = 'purple';
                                else if (status === 'FAILED') color = 'red';
                                return <Tag color={color} className="tw-rounded-md tw-font-semibold">{status}</Tag>;
                              }
                            },
                            {
                              title: 'SENT AT',
                              dataIndex: 'sentAt',
                              key: 'sentAt',
                              width: '23%',
                              render: (val) => val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : '-'
                            },
                            {
                              title: 'ERROR REASON',
                              dataIndex: 'errorMessage',
                              key: 'errorMessage',
                              width: '20%',
                              render: (val) => {
                                if (!val) return '-';
                                let displayMsg = val;
                                const v = val.toUpperCase();
                                if (v.includes('MESSAGING_LIMIT') || v.includes('131048') || v.includes('131056')) {
                                  displayMsg = 'Messaging Limit Reached. Meta has temporarily limited the number of new customer conversations.';
                                } else if (v.includes('INVALID_NUMBER') || v.includes('131026')) {
                                  displayMsg = 'Invalid WhatsApp Number.';
                                } else if (v.includes('TEMPLATE_REJECTED') || v.includes('132015') || v.includes('132001')) {
                                  displayMsg = 'Template Rejected by Meta.';
                                } else if (v.includes('USER_BLOCKED') || v.includes('131031')) {
                                  displayMsg = 'Blocked User.';
                                }
                                
                                return (
                                  <Tooltip title={val}>
                                    <span className="tw-text-red-500 tw-text-xs tw-font-medium">{displayMsg}</span>
                                  </Tooltip>
                                );
                              }
                            }
                          ]}
                        />
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
      </div>
    </div>
  );
};

export default WhatsAppCampaigns;
